/* eslint-disable react/prop-types */
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIconButton from '../../components/DeleteIconButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, CircularProgress, FormControl, FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Popover, Radio, RadioGroup, Select, Switch, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TimePicker } from '../../components/TimePicker';
import moment from 'moment';
import React, { useEffect } from 'react';
import { STANDARD_MAP_PIN_ICON_COMPONENTS, STANDARD_MAP_PIN_KEYS } from './mapPinConfig';
import useMapPinIconLibrary from './mapPinIconLibrary';

import { useSnackbar } from 'notistack';
import images from "../../components/images";
import StyledFilledTextField from '../../components/StyledFilledTextField';
import StyledRemoteAutocomplete from '../../components/StyledRemoteAutocomplete';
import useConfirmDialog from '../../components/useConfirmDialog';
import { isOk, serverErrorMessage } from '../../utils/errors';
import { ContextStore } from '../../store/contextStore';
import { asJSONPath, useIsWidthUp } from '../../utils';
import { validateTripActivity } from '../../utils/validators/trip';
import validURL from '../../utils/validators/url';
import { getCurrencyFromActivities } from '../utils';
import TripFlowActivity from './TripEditFlowActivity';

const useStyles = makeStyles((theme) => ({
    root: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginLeft: 0,
        marginRight: 0,
        border: 'solid 1px #D8D8D8',
        borderRadius: '16px',
        padding: '0.25rem 0rem 0.5rem 0',
        [theme.breakpoints.down('sm')]: {
            padding: '0.25rem 0 0.5rem 0rem',
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
            border: 'solid 1px #d8d8d8 !important',
            '& .MuiGrid2-container': {
                marginLeft: '0',
            },
        },
    },
    slotIndexIconWrapper: {
        position: 'relative',
        width: '45px',
        // height: '45px',
    },
    slotIndexIconText: {
        position: 'absolute',
        left: 0,
        top: 3,
        color: 'white',
        textAlign: 'center',
        width: '28px',
    },
    pinIconContainer: {
        width: '20px',
        display: 'flex',
        alignItems: 'center',
    },
}));

const emptyActivity = {
    activity: '',
    booking_state: null,
    confirmation: null,
    cost_max: null,
    cost_min: null,
    cost_prefix: null,
    cost_unit: null,
    currency: null,
    id: null,
    is_selected: false,
    payment_type: 0,
    price_level: null,
    price_type: 0,
    sequence: 1,
};

const NumberedLocationIcon = (props) => {
    const { classes, number } = props;
    return (
        <Grid item xs={1} lg={'auto'} style={{ width: '20px', paddingTop: '0.5rem' }}>
            <div className={classes.slotIndexIconWrapper}>
                <svg width="28" height="32" viewBox="0 0 28 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M14 0C17.713 0 21.274 1.44489 23.8995 4.01682C26.525 6.58875 28 10.077 28 13.7143C28 19.392 23.5822 25.341 14.9333 31.6952C14.6641 31.8931 14.3366 32 14 32C13.6634 32 13.3359 31.8931 13.0667 31.6952C4.41778 25.341 0 19.392 0 13.7143C0 10.077 1.475 6.58875 4.1005 4.01682C6.72601 1.44489 10.287 0 14 0Z"
                        fill="#007AFF"
                    />
                </svg>
                <Typography className={classes.slotIndexIconText}>{number}</Typography>
            </div>
        </Grid>
    );
};

const IconLocationPin = ({ iconKey, iconUrl }) => {
    const IconComp = iconKey ? STANDARD_MAP_PIN_ICON_COMPONENTS[iconKey] : null;
    return (
        <Grid item xs={1} lg={'auto'} style={{ width: '20px', paddingTop: '0.5rem' }}>
            <div style={{ position: 'relative', width: '45px', height: '45px', marginTop: '4px' }}>
                <svg width="28" height="32" viewBox="0 0 28 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 0C17.713 0 21.274 1.44489 23.8995 4.01682C26.525 6.58875 28 10.077 28 13.7143C28 19.392 23.5822 25.341 14.9333 31.6952C14.6641 31.8931 14.3366 32 14 32C13.6634 32 13.3359 31.8931 13.0667 31.6952C4.41778 25.341 0 19.392 0 13.7143C0 10.077 1.475 6.58875 4.1005 4.01682C6.72601 1.44489 10.287 0 14 0Z" fill="#007AFF" />
                </svg>
                <div style={{ position: 'absolute', left: 0, top: 0, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {IconComp ? (
                        <IconComp sx={{ color: 'white', fontSize: 18 }} />
                    ) : iconUrl ? (
                        <Box sx={{ width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img alt="pin" src={iconUrl} style={{ width: 18, height: 18, objectFit: 'contain', display: 'block', filter: 'brightness(0) invert(1)' }} />
                        </Box>
                    ) : null}
                </div>
            </div>
        </Grid>
    );
};

const TripFlowTimeSlot = (props) => {
    const classes = useStyles();
    const time = moment(props.slot.time, 'HH:mm:ss');
    const activities = props.slot.activities || [];
    const [open, setOpen] = React.useState(Boolean(props.slot.open));
    const [errors, setErrors] = React.useState({});
    const isSmallScreen = !useIsWidthUp('sm');
    const isXLScreen = useIsWidthUp('lg');
    const { settings, hasPerm } = React.useContext(ContextStore);
    const defaultActivityCurrency = getCurrencyFromActivities(props.trip);
    // Helper to show human-friendly labels for standard pin keys
    const formatPinKeyLabel = React.useCallback((key) => {
        if (!key || typeof key !== 'string') return '';
        const spaced = key.replace(/_/g, ' ');
        return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    }, []);


    // Local state for Map Pin popover
    const [pinAnchorEl, setPinAnchorEl] = React.useState(null);
    const pinPopoverOpen = Boolean(pinAnchorEl);
    const handleOpenPinPopover = (event) => setPinAnchorEl(event.currentTarget);
    const handleClosePinPopover = () => setPinAnchorEl(null);

    // Upload state for custom PNG icon
    const { enqueueSnackbar } = useSnackbar();
    const [isUploadingIcon, setIsUploadingIcon] = React.useState(false);
    const [pendingIconPreview, setPendingIconPreview] = React.useState(null);
    const [, setLastCustomIconUrl] = React.useState(null);
    const iconLibrary = useMapPinIconLibrary(props.trip?.id);
    const [confirmRemoveIcon, confirmRemoveIconDialog] = useConfirmDialog();
    const fileInputRef = React.useRef(null);
    // Local description state and debounce to reduce re-renders on typing
    const [localDescription, setLocalDescription] = React.useState(props.slot.description || '');
    const descDebounceRef = React.useRef(null);

    const STANDARD_PIN_ICONS = STANDARD_MAP_PIN_KEYS.map((key) => ({ key, Icon: STANDARD_MAP_PIN_ICON_COMPONENTS[key] }));

    const pinType = props.slot.pin_type || 'numbered'; // 'numbered' | 'icon'
    const pinNumberMode = props.slot.pin_number_mode || 'sequential'; // 'sequential' | 'custom'
    const pinCustomNumber = props.slot.pin_custom_number || null;
    // CP-6763: the user erased the custom number. The field shows it empty, and the trip cannot be saved
    // until a number is entered (validateRequiredTripConditions). A stop saved without a number keeps
    // showing its sequential one, as before, and does not block the save.
    const pinCustomNumberEmpty = pinType === 'numbered' && pinNumberMode === 'custom' && Boolean(props.slot._pin_custom_number_empty);
    const pinIconKey = props.slot.pin_icon_key || null;
    const pinIconUrl = props.slot.pin_icon_url || null; // png url

    const setSlotPatch = (patch) => {
        props.onChange && props.onChange(patch, props.idx);
    };

    // CP-7151: the operator's icon library (shared by all its tours), plus any custom icon a stop of
    // this trip uses that the library no longer offers, so it stays selectable here.
    const collectTripCustomIcons = React.useCallback(() => {
        try {
            const days = props.trip?.itinerary?.days || [];
            const urls = new Set();
            days.forEach((d) => (d?.timeslots || []).forEach((ts) => {
                if (ts && ts.pin_type === 'icon' && ts.pin_icon_url) {
                    urls.add(ts.pin_icon_url);
                }
            }));
            return Array.from(urls);
        } catch (_) { return []; }
    }, [props.trip]);
    const customIcons = React.useMemo(() => {
        const byUrl = new Map(iconLibrary.icons.map((icon) => [icon.url, icon]));
        collectTripCustomIcons().forEach((url) => {
            if (!byUrl.has(url)) byUrl.set(url, { url });
        });
        return Array.from(byUrl.values());
    }, [iconLibrary.icons, collectTripCustomIcons]);
    const canRemoveIcons = !props.disabled && hasPerm('tour', 'write');

    // replaced by button toggles

    const handleNumberModeChange = (event) => {
        const value = event.target.value;
        setSlotPatch({ pin_number_mode: value, _pin_custom_number_empty: false });
        if (value === 'sequential') {
            setSlotPatch({ pin_custom_number: null });
        } else if (value === 'custom' && (pinCustomNumber === null || pinCustomNumber === undefined)) {
            setSlotPatch({ pin_custom_number: props.idx + 1 });
        }
    };

    const handleCustomNumberInput = (event) => {
        const value = event.target.value === '' ? null : parseInt(event.target.value, 10);
        if (value === null || Number.isNaN(value)) {
            setSlotPatch({ pin_custom_number: null, _pin_custom_number_empty: true });
        } else {
            setSlotPatch({ pin_custom_number: Math.max(1, value), _pin_custom_number_empty: false });
        }
    };

    const handleSelectStandardIcon = (key) => {
        // Selecting a standard icon must clear any custom URL
        setSlotPatch({ pin_type: 'icon', pin_icon_key: key, pin_icon_url: null });
        handleClosePinPopover();
    };

    const onPickCustomIconClick = () => {
        if (props.disabled || isUploadingIcon) return;
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const onCustomIconFileChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        const isPng = (file.type && file.type.toLowerCase().includes('png')) || /\.png$/i.test(file.name || '');
        if (!isPng) {
            enqueueSnackbar('Please select a PNG image', { variant: 'error' });
            return;
        }
        setPendingIconPreview(URL.createObjectURL(file));
        setIsUploadingIcon(true);
        try {
            // joins the operator's library (useMapPinIconLibrary), so every stop's picker offers it
            const res = await iconLibrary.upload(file);
            if (res.statusCode === 200 || res.statusCode === 201) {
                const url = res?.data?.url
                    || (res?.data?.image ? images.url(settings.images.base, res.data.image) : null)
                    || (typeof res?.data === 'string' && validURL(res.data) ? res.data : null);
                if (url) {
                    setLastCustomIconUrl(url);
                    setSlotPatch({ pin_type: 'icon', pin_icon_key: null, pin_icon_url: url });
                    enqueueSnackbar('Icon uploaded', { variant: 'success' });
                    handleClosePinPopover();
                } else {
                    enqueueSnackbar('Upload succeeded, but URL is missing in response', { variant: 'warning' });
                }
            } else {
                enqueueSnackbar(res?.data?.message || 'Error uploading icon', { variant: 'error' });
            }
        } catch (err) {
            enqueueSnackbar('Upload failed', { variant: 'error' });
        } finally {
            setIsUploadingIcon(false);
            setPendingIconPreview(null);
        }
    };

    const handleClearSelectedIcon = () => {
        // Keep the last uploaded custom icon cached locally for re-selection
        setSlotPatch({ pin_icon_key: null, pin_icon_url: null, pin_type: 'icon' });
    };

    const currentMapPinSummary = (() => {
        if (pinType === 'numbered') return 'Numbered pin';
        if (pinType === 'icon') {
            if (pinIconKey) return formatPinKeyLabel(pinIconKey);
            if (pinIconUrl) return 'Custom icon';
            return 'Icon pin';
        }
        return 'Map pin';
    })();

    useEffect(() => {
        // update local state based on parent trip state only if explicit boolean provided
        if (typeof props.slot.open === 'boolean') {
            setOpen(props.slot.open);
        }
        // cache last custom icon url if provided from server/state
        if (pinIconUrl) {
            setLastCustomIconUrl(pinIconUrl);
        }
    }, [props.slot, pinIconUrl]);

    // Re-read the library when the picker opens: another editor may have added or removed icons
    const { refresh: refreshIconLibrary } = iconLibrary;
    useEffect(() => {
        if (pinPopoverOpen) refreshIconLibrary();
    }, [pinPopoverOpen, refreshIconLibrary]);

    const onRemoveCustomIcon = async (event, icon) => {
        event.stopPropagation();
        const ok = await confirmRemoveIcon({
            title: 'Remove icon from library',
            body: 'This icon will no longer be offered on any of your tours. Stops that already use it keep it.',
            okButtonText: 'Remove',
            destructive: true,
        });
        if (!ok) return;
        try {
            const res = await iconLibrary.remove(icon);
            if (isOk(res)) {
                enqueueSnackbar('Icon removed from library', { variant: 'success' });
            } else {
                enqueueSnackbar(serverErrorMessage(res, 'Could not remove the icon'), { variant: 'error' });
            }
        } catch (err) {
            enqueueSnackbar('Could not remove the icon', { variant: 'error' });
        }
    };

    // Keep local description in sync with external value
    useEffect(() => {
        const next = props.slot.description || '';
        if (next !== localDescription) {
            setLocalDescription(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.slot.description]);

    const onLocalDescriptionChange = (value) => {
        setLocalDescription(value);
        if (descDebounceRef.current) clearTimeout(descDebounceRef.current);
        descDebounceRef.current = setTimeout(() => {
            props.onChange && props.onChange({ description: value }, props.idx);
        }, 250);
    };

    const onLocalDescriptionBlur = () => {
        if (descDebounceRef.current) {
            clearTimeout(descDebounceRef.current);
            descDebounceRef.current = null;
        }
        props.onChange && props.onChange({ description: localDescription }, props.idx);
    };

    const onTimeChange = (time) => {
        const timestr = time.format('HH:mm:ss');
        const timeExists = props.day.timeslots.filter((x, idx) => x.id !== props.slot.id && idx !== props.slot.idx && x.time === timestr).length > 0;
        if (timeExists) {
            setErrors((prevState) => ({ ...prevState, time: { error: true, helperText: 'Time must be unique' } }));
            props.onValidate && props.onValidate(`timeslots[${props.idx}].time`, false);
        } else {
            props.onChange &&
                props.onChange(
                    {
                        time: time.format('HH:mm:ss'),
                    },
                    props.idx
                );
            setErrors((prevState) => {
                const ns = { ...prevState };
                delete ns.time;
                return ns;
            });
            props.onValidate && props.onValidate(`timeslots[${props.idx}].time`, null);
        }
    };

    const onActivityChange = (act, idx) => {
        props.onChange && props.onChange(asJSONPath(`activities[${idx}]`, act), props.idx);
    };

    const onActivityDelete = (act, idx) => {
        props.onChange &&
            props.onChange(
                {
                    activities: activities.filter((a, i) => i !== idx),
                },
                props.idx
            );
    };

    const onActivityAdd = () => {
        const lastActivity = activities[activities.length - 1];
        const nextSeq = lastActivity && lastActivity.sequence ? lastActivity.sequence + 1 : 1;
        const newAct = { ...emptyActivity, currency: defaultActivityCurrency, sequence: nextSeq };
        props.onChange && props.onChange(asJSONPath(`activities[${activities.length}]`, newAct), props.idx);
        const { canSave, errors } = validateTripActivity(newAct);
        onValidateActivity(`activities[${activities.length - 1}]`, canSave, errors);
    };

    const onDelete = () => {
        props.onDelete && props.onDelete(props.slot, props.idx);
    };

    const onShowTransportChange = () => {
        props.onChange &&
            props.onChange(
                {
                    show_transport: !props.slot.show_transport,
                },
                props.idx
            );
    };

    // const onTransportPerActivityChange = (ev) => {
    //     props.onChange &&
    //         props.onChange(
    //             {
    //                 transport_per_activity: !props.slot.transport_per_activity,
    //             },
    //             props.idx
    //         );
    // };

    const onNextTransportChange = (ev, nv) => {
        props.onChange &&
            props.onChange(
                {
                    next_transport: nv,
                },
                props.idx
            );
    };

    const onTextChange = (ev) => {
        if (ev.target.name === 'timeline_text') {
            props.onChange &&
                props.onChange(
                    {
                        [ev.target.name]: ev.target.value.trim() === '' ? null : ev.target.value,
                    },
                    props.idx
                );

            return;
        }
        props.onChange &&
            props.onChange(
                {
                    [ev.target.name]: ev.target.value,
                },
                props.idx
            );
    };

    const onValidateActivity = (path, canSave, errors) => {
        props.onValidate && props.onValidate(`timeslots[${props.idx}].${path}`, canSave, errors);
    };

    let activityList = activities.sort((a, b) => (a.sequence > b.sequence ? 1 : -1));
    activityList = activityList.map((a, idx) => (
        <TripFlowActivity
            activities={activities}
            key={idx}
            activity={a}
            idx={idx}
            enableTransport={props.slot.transport_per_activity}
            slot={props.slot}
            trip={props.trip}
            onChange={onActivityChange}
            onDelete={onActivityDelete}
            onValidate={onValidateActivity}
            isSmallScreen={props.isSmallScreen}
            setNewDocsAdded={props.setNewDocsAdded}
            newDocsAdded={props.newDocsAdded}
            setClientErrors={props.setClientErrors}
            disabled={props.disabled}
        />
    ));

    const onTimelineDisplaySelect = (ev) => {
        props.onChange &&
            props.onChange(
                {
                    timeline_display: ev.target.value,
                },
                props.idx
            );
    };

    return (
        <div className={classes.root} style={{ visibility: props.visibility ? 'visible' : 'hidden', height: props.visibility ? 'auto' : '50px' }}>
            <Grid container direction="row" columnSpacing={0.5} rowSpacing={0} pr={0} alignItems={'center'} justifyContent="flex-start">
                <Grid item xs={11} container columnSpacing={0.5} rowSpacing={0} alignItems="center">
                    <Grid item xs={'auto'} sm={1} style={{ width: isSmallScreen ? '40px' : '20px', display: 'flex', alignItems: 'center' }}>
                        <Tooltip title={open ? 'Collapse' : 'Expand'}>
                            <IconButton color="primary" onClick={() => setOpen(!open)}>
                                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={'auto'} sm={1} className={classes.pinIconContainer}>
                        {pinType === 'icon' ? (
                            <IconLocationPin iconKey={pinIconKey} iconUrl={pinIconUrl} />
                        ) : (
                            <NumberedLocationIcon classes={classes} number={(pinNumberMode === 'custom' ? (pinCustomNumber ?? props.idx + 1) : props.idx + 1)} />
                        )}
                    </Grid>
                    {!isSmallScreen && (
                        <Grid item sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <StyledFilledTextField
                                disabled={props.disabled}
                                variant="filled"
                                size="small"
                                fullWidth
                                label={'Map pin'}
                                value={currentMapPinSummary}
                                onClick={handleOpenPinPopover}
                                error={pinCustomNumberEmpty}
                                sx={{ mt: 0.75 }}
                                InputProps={{
                                    readOnly: true,
                                    startAdornment: pinType === 'icon' && (pinIconKey || pinIconUrl) ? (
                                        <InputAdornment position="start">
                                            {pinIconKey ? (
                                                (() => { const Icon = STANDARD_MAP_PIN_ICON_COMPONENTS[pinIconKey]; return Icon ? <Icon fontSize="small" /> : null; })()
                                            ) : pinIconUrl ? (
                                                <img alt="pin" src={pinIconUrl} style={{ width: 18, height: 18, objectFit: 'contain', display: 'block' }} />
                                            ) : null}
                                        </InputAdornment>
                                    ) : undefined,
                                    endAdornment: pinType === 'icon' && (pinIconKey || pinIconUrl) ? (
                                        <InputAdornment position="end">
                                            <Tooltip title="Clear icon">
                                                <span style={{ display: 'inline-flex' }}>
                                                    <IconButton size="small" onClick={handleClearSelectedIcon} disabled={props.disabled}>
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </InputAdornment>
                                    ) : (
                                        <InputAdornment position="end">
                                            <ArrowDropDownIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    )}
                    <Grid item xs={8} sm={2} style={{ display: 'flex', alignItems: 'center' }}>
                        <TimePicker
                            disabled={props.disabled}
                            label="Time"
                            inputVariant="filled"
                            size="small"
                            value={time}
                            onChange={onTimeChange}
                            ampm={true}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <AccessTimeIcon />
                                    </InputAdornment>
                                ),
                            }}
                            slotProps={{
                                textField: {
                                    variant: 'filled',
                                    onKeyDown: (e) => e.preventDefault(),
                                    size: 'small',
                                    fullWidth: true,
                                    sx: { mt: 0.75 },
                                },
                                actionBar: {
                                    actions: ['accept'],
                                },
                            }}
                            {...errors.time}
                        />
                    </Grid>
                    {!isSmallScreen && <>
                        <Grid item sm={2} style={{ display: 'flex', alignItems: 'center' }}>
                            <FormControl fullWidth variant="filled" size="small" disabled={props.disabled} sx={{ mt: 0.75 }}>
                                <InputLabel id="time-label-select">Time Label</InputLabel>
                                <Select labelId="time-label-select" value={props.slot.timeline_display} onChange={onTimelineDisplaySelect}>
                                    <MenuItem value={0}>Show Label</MenuItem>
                                    <MenuItem value={1}>Show Time</MenuItem>
                                    <MenuItem value={2}>Hide</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                            {props.slot.timeline_display === 0 && <StyledFilledTextField disabled={props.disabled} variant="filled" size="small" fullWidth name="timeline_text" label={'Text Label'} value={props.slot.timeline_text || ''} onChange={onTextChange} sx={{ mt: 0.75 }} />}{' '}
                        </Grid>
                    </>}
                </Grid>
                <Grid item xs={1} container justifyContent="flex-end" style={{ display: 'flex', alignItems: 'center', marginLeft: -16 }}>
                    <DeleteIconButton title="Remove stop" perm="tour.write" size="medium" fontSize="medium" tabIndex={-1} disabled={props.disabled} onClick={onDelete} />
                </Grid>
                <Popover
                    open={pinPopoverOpen}
                    anchorEl={pinAnchorEl}
                    onClose={handleClosePinPopover}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    <Box sx={{ p: 2.5, width: { xs: 'calc(100vw - 80px)', sm: 380 }, maxWidth: 'calc(100vw - 32px)' }}>
                        {/* Segmented tabs */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                            <Button onClick={() => setSlotPatch({ pin_type: 'numbered' })}
                                sx={{
                                    flex: 1, height: 38, borderRadius: '20px', fontWeight: 700, letterSpacing: 0.5,
                                    bgcolor: pinType === 'numbered' ? 'primary.main' : '#EAEAEA', color: pinType === 'numbered' ? '#fff' : '#202020',
                                    '&:hover': { bgcolor: pinType === 'numbered' ? 'primary.dark' : '#E0E0E0' }
                                }}>
                                NUMBERED PIN
                            </Button>
                            <Button onClick={() => setSlotPatch({ pin_type: 'icon' })}
                                sx={{
                                    flex: 1, height: 38, borderRadius: '20px', fontWeight: 700, letterSpacing: 0.5,
                                    bgcolor: pinType === 'icon' ? 'primary.main' : '#EAEAEA', color: pinType === 'icon' ? '#fff' : '#202020',
                                    '&:hover': { bgcolor: pinType === 'icon' ? 'primary.dark' : '#E0E0E0' }
                                }}>
                                ICON PIN
                            </Button>
                        </Box>
                        {pinType === 'icon' && (
                            <Typography variant="subtitle1" sx={{ fontWeight: 400, mb: 1.25 }}>
                                Select an existing icon or upload a custom one:
                            </Typography>
                        )}
                        {pinType === 'numbered' && (
                            <Box>
                                <RadioGroup value={pinNumberMode} onChange={handleNumberModeChange}>
                                    <FormControlLabel value="sequential" control={<Radio />} label="Sequential order" />
                                    <FormControlLabel value="custom" control={<Radio />} label="Custom numbering" />
                                </RadioGroup>
                                {pinNumberMode === 'custom' && (
                                    <Box sx={{ mt: 0 }}>
                                        <StyledFilledTextField
                                            type="number"
                                            size="small"
                                            variant="filled"
                                            value={pinCustomNumberEmpty ? '' : (pinCustomNumber ?? props.idx + 1)}
                                            onChange={handleCustomNumberInput}
                                            error={pinCustomNumberEmpty}
                                            helperText={pinCustomNumberEmpty ? 'Enter a number' : undefined}
                                            inputProps={{ min: 1, step: 1, 'aria-label': 'Custom number' }}
                                            sx={{ width: 140, mt: 0, '& .MuiFilledInput-input': { paddingTop: '6px', paddingBottom: '6px' } }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                        {pinType === 'icon' && (
                            <Box>
                                <Grid container spacing={1.25} sx={{ mt: 1 }}>
                                    {STANDARD_PIN_ICONS.map(({ key, Icon }) => (
                                        <Grid item xs={4} key={key}>
                                            <Box
                                                onClick={() => handleSelectStandardIcon(key)}
                                                sx={{
                                                    border: '1px solid #D8D8D8',
                                                    borderRadius: '10px',
                                                    px: 0.5,
                                                    py: 0.5,
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    backgroundColor: pinIconKey === key ? 'rgba(0,122,255,0.08)' : 'transparent',
                                                }}
                                            >
                                                <Icon />
                                                <Typography variant="body2" display="block" sx={{ mt: 0.25, fontSize: { xs: '0.55rem', sm: '0.65rem' }, lineHeight: 1.2 }}>
                                                    {formatPinKeyLabel(key)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                    {customIcons.map((icon) => (
                                        <Grid item xs={4} key={`custom-icon-${icon.url}`}>
                                            <Box
                                                onClick={() => { setSlotPatch({ pin_type: 'icon', pin_icon_key: null, pin_icon_url: icon.url }); handleClosePinPopover(); }}
                                                title={icon.name || undefined}
                                                sx={{
                                                    position: 'relative',
                                                    border: '1px solid #D8D8D8',
                                                    borderRadius: '10px',
                                                    px: 0.5,
                                                    py: 0.5,
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    backgroundColor: (!pinIconKey && pinIconUrl === icon.url) ? 'rgba(0,122,255,0.08)' : 'transparent',
                                                }}
                                            >
                                                {icon.id && canRemoveIcons && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Remove icon from library"
                                                        title="Remove from library"
                                                        onClick={(e) => onRemoveCustomIcon(e, icon)}
                                                        sx={{ position: 'absolute', top: 0, right: 0, p: 0.25 }}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                )}
                                                <Box sx={{ width: 24, height: 24, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <img alt="custom pin" src={icon.url} style={{ width: 24, height: 24, objectFit: 'contain', display: 'block' }} />
                                                </Box>
                                                <Typography variant="body2" display="block" noWrap sx={{ mt: 0.25, px: 1.5, fontSize: { xs: '0.55rem', sm: '0.65rem' }, lineHeight: 1.2 }}>
                                                    {icon.name || 'Custom'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                                <Box sx={{ mt: 2 }}>
                                    <input ref={fileInputRef} type="file" accept="image/png,.png" style={{ display: 'none' }} onChange={onCustomIconFileChange} />
                                    <Button fullWidth variant="outlined" onClick={onPickCustomIconClick} disabled={props.disabled || isUploadingIcon} startIcon={!isUploadingIcon ? <AddIcon /> : undefined} sx={{ borderStyle: 'solid', fontWeight: 700 }}>
                                        {isUploadingIcon ? (<><CircularProgress size={16} sx={{ mr: 1 }} /> Uploading...</>) : 'UPLOAD CUSTOM ICON'}
                                    </Button>
                                    <Typography variant="caption" sx={{ mt: 0.5, fontStyle: 'italic', textAlign: 'center', display: 'block', color: 'text.secondary' }}>
                                        PNG with a transparent background, square (e.g. 24×24 px)
                                    </Typography>
                                    {pendingIconPreview && (
                                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                            <Typography variant="caption">Preview:</Typography>
                                            <img alt="preview" src={pendingIconPreview} style={{ width: 18, height: 18, objectFit: 'contain', display: 'block' }} />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                        {/* CP-6763: closes the dialog the way the time picker's OK does */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, mb: -1 }}>
                            <Button onClick={handleClosePinPopover} disabled={pinCustomNumberEmpty}>OK</Button>
                        </Box>
                    </Box>
                </Popover>
                {confirmRemoveIconDialog}
                {isSmallScreen && open && <>
                    <Grid item xs={12} ml={2} sx={{ width: 'calc(100% - 32px)', flexBasis: 'unset', mt: 1, mb: 1 }} >
                        <FormControl fullWidth variant="filled" size="small" disabled={props.disabled} >
                            <InputLabel id="time-label-select">Time Label</InputLabel>
                            <Select labelId="time-label-select" value={props.slot.timeline_display} onChange={onTimelineDisplaySelect}>
                                <MenuItem value={0}>Show Label</MenuItem>
                                <MenuItem value={1}>Show Time</MenuItem>
                                <MenuItem value={2}>Hide</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} ml={2} sx={{ width: 'calc(100% - 32px)', flexBasis: 'unset', mb: 1 }} >
                        {props.slot.timeline_display === 0 && <StyledFilledTextField disabled={props.disabled} variant="filled" size="small" fullWidth name="timeline_text" label={'Text Label'} value={props.slot.timeline_text || ''} onChange={onTextChange} />}{' '}
                    </Grid>
                    <Grid item xs={12} ml={2} sx={{ width: 'calc(100% - 32px)', flexBasis: 'unset', mb: 1 }}>
                        <StyledFilledTextField
                            disabled={props.disabled}
                            variant="filled"
                            size="small"
                            fullWidth
                            label={'Map pin'}
                            value={currentMapPinSummary}
                            onClick={handleOpenPinPopover}
                            error={pinCustomNumberEmpty}
                            InputProps={{
                                readOnly: true,
                                startAdornment: pinType === 'icon' && (pinIconKey || pinIconUrl) ? (
                                    <InputAdornment position="start">
                                        {pinIconKey ? (
                                            (() => { const Icon = STANDARD_MAP_PIN_ICON_COMPONENTS[pinIconKey]; return Icon ? <Icon fontSize="small" /> : null; })()
                                        ) : pinIconUrl ? (
                                            <img alt="pin" src={pinIconUrl} style={{ width: 18, height: 18, objectFit: 'contain', display: 'block' }} />
                                        ) : null}
                                    </InputAdornment>
                                ) : undefined,
                                endAdornment: pinType === 'icon' && (pinIconKey || pinIconUrl) ? (
                                    <InputAdornment position="end">
                                        <Tooltip title="Clear icon">
                                            <span style={{ display: 'inline-flex' }}>
                                                <IconButton size="small" onClick={handleClearSelectedIcon} disabled={props.disabled}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : (
                                    <InputAdornment position="end">
                                        <ExpandMoreIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </>}
            </Grid>
            {open ? (
                <Grid container direction="row" spacing={isSmallScreen ? 0 : 1} p={isSmallScreen ? 2 : 0}>
                    {isXLScreen && <Grid item style={{ width: '50px' }}></Grid>}
                    <Grid item xs={12} lg={11}>
                        <StyledFilledTextField
                            disabled={props.disabled}
                            multiline
                            rows={3}
                            variant="filled"
                            size="small"
                            fullWidth
                            name="description"
                            label={'Description (Optional)'}
                            value={localDescription}
                            onChange={(e) => onLocalDescriptionChange(e.target.value)}
                            onBlur={onLocalDescriptionBlur}
                            style={{ margin: isSmallScreen ? 0 : '0.5rem 0' }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {activityList}
                        {!props.disabled && <Button style={{ marginLeft: props.isSmallScreen ? '0' : '50px', marginTop: '0.5rem', marginBottom: props.isLastItem ? '0.5rem' : '0' }} color="primary" variant="outlined" onClick={onActivityAdd}>
                            Add activity
                        </Button>}
                    </Grid>
                    {!props.isLastItem && (<>
                        <Grid item xs={12} sm={12} container alignItems="center" style={{ paddingLeft: props.isSmallScreen ? '10px' : '60px' }}>
                            <FormGroup >
                                <FormControlLabel control={<Switch disabled={props.disabled} onChange={onShowTransportChange} checked={props.slot.show_transport} />} />
                            </FormGroup>
                            <Typography ml={-1}>Transportation to the next stop</Typography>
                        </Grid>
                        {props.slot.show_transport && (
                            <Grid item xs={12} lg={8} container spacing={2} pb={2} ml={props.isSmallScreen ? '-10px' : '40px'}>
                                <Grid item xs={6} sm={5}>
                                    <StyledRemoteAutocomplete
                                        disabled={props.disabled}
                                        customInputIcon
                                        textFieldProps={{
                                            fullWidth: true,
                                        }}
                                        resource="transport_type"
                                        renderOption={(props, option) => {
                                            return (
                                                <Box component="li" sx={{ '& > img, & > .placeholder': { mr: 2, flexShrink: 0, width: 20 } }} {...props}>
                                                    {
                                                        option.icon ? (
                                                            <img loading="lazy" width="20" src={images.url(settings.images.base, option.icon)} alt={option.name} />
                                                        ) : (
                                                            <span className="placeholder" />
                                                        )
                                                    }
                                                    <Typography>{option.name}</Typography>
                                                </Box>
                                            );
                                        }}
                                        urlSuffix={null}
                                        label="Transportation"
                                        variant="filled"
                                        size="small"
                                        onChange={onNextTransportChange}
                                        value={props.slot.next_transport}
                                        optionsResultsSize={1000}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={7}>
                                    <StyledFilledTextField disabled={!props.slot.show_transport || props.disabled} variant="filled" size="small" fullWidth name="transport_text" label={props.isSmallScreen ? 'Text' : 'Transport Text (optional)'} value={props.slot.transport_text || ''} onChange={onTextChange} />
                                </Grid>
                            </Grid>
                        )}
                    </>)}
                </Grid>
            ) : null}
        </div>
    );
};

export default TripFlowTimeSlot;

